import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { eqlAggregate, eqlQueryAll } from "../../../../apis/apiEQL";
import { useReportContext } from "../../../Report/ReportContext/useReportContext";

// Fallback categorical palette (mirrors EChart's default).
const DEFAULT_COLORS = ["#009ed7", "#44cb67", "#ffaf00", "#de463d", "#1a4792", "#007b6f"];

// Severity: fixed order + semantic color. Labels come from severityOptions.
const SEVERITY_ORDER = ["type::hight", "type::medium", "type::low", "type::null", "type::other"];
const SEVERITY_COLOR = {
  "type::hight": "#de463d", // Alta
  "type::medium": "#ffaf00", // Media
  "type::low": "#44cb67", // Baja
  "type::null": "#9aa4b2", // Sin definir
  "type::other": "#009ed7", // Otra
};

// Closure type (status.details.closed) breakdown for closed reports. A null
// closure means the report is still being concluded ("en proceso de cierre").
const CLOSURE_ORDER = ["founded", "unfounded", "undefined", "inadmissible", "in_process"];
const CLOSURE_COLOR = {
  founded: "#007b6f",
  unfounded: "#9aa4b2",
  undefined: "#ffaf00",
  inadmissible: "#de463d",
  in_process: "#1a4792",
};

const yearFilter = (year) => ({
  AND: [
    { field: "created_at", operator: "gte", value: `${year}-01-01` },
    { field: "created_at", operator: "lte", value: `${year}-12-31` },
  ],
});

/**
 * useStaticTrends — real data for the static dashboard's trend charts, for a
 * given year. Returns four charts (by month, status, severity, channel) in the
 * shape TrendChartCard/EChart expect: { data:[{label,count}], type, title }.
 *
 * Monthly data is accumulated per year, so once more than one year has been
 * loaded the "by month" chart overlays one line per year for comparison. Every
 * year's data is cached and only the selected year is ever queried.
 *
 * @param {number} year
 * @returns {{ trends: Array, loading: boolean }}
 */
export const useStaticTrends = (year) => {
  const [t] = useTranslation("dashboard");
  const { status_locales, severityOptions, entryways, optionsMonth } = useReportContext();
  const ctxRef = useRef({ status_locales, severityOptions, entryways, optionsMonth });
  ctxRef.current = { status_locales, severityOptions, entryways, optionsMonth };

  const monthCacheRef = useRef(new Map()); // year -> [{label,count}] (12 months)
  const otherCacheRef = useRef(new Map()); // year -> { statusChart, severityChart, channelChart }
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    // Builds the four charts for `year`. The by-month chart carries one series
    // per already-loaded year (ascending) so multiple years overlay; its `data`
    // is the selected year's months (single-series fallback in EChart).
    const buildTrends = () => {
      const loadedYears = [...monthCacheRef.current.keys()].sort((a, b) => a - b);
      const series = loadedYears.map((y) => ({
        name: String(y),
        data: monthCacheRef.current.get(y),
      }));
      const selectedMonths = monthCacheRef.current.get(year) || [];
      const { statusChart, severityChart, channelChart } = otherCacheRef.current.get(year);

      return [
        {
          id: "by-month",
          title: t("static_dashboard.trends_by_month"),
          type: "line",
          chartData: {
            title: t("static_dashboard.trends_by_month"),
            type: "normal",
            data: selectedMonths,
            series,
          },
        },
        statusChart,
        severityChart,
        channelChart,
      ];
    };

    (async () => {
      const needMonth = !monthCacheRef.current.has(year);
      const needOther = !otherCacheRef.current.has(year);

      // Everything cached → rebuild synchronously (no spinner, no refetch).
      if (!needMonth && !needOther) {
        setTrends(buildTrends());
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const filters = yearFilter(year);

        if (needMonth) {
          const monthRows = await eqlQueryAll({ fields: ["created_at"], filters });
          if (!active) return;
          const { optionsMonth: months } = ctxRef.current;
          const counts = {};
          (monthRows?.rows || []).forEach((r) => {
            const d = r?.created_at;
            if (!d) return;
            const mm = String(d).slice(5, 7); // "01".."12"
            counts[mm] = (counts[mm] || 0) + 1;
          });
          monthCacheRef.current.set(
            year,
            months.map((o) => ({ label: o.label, count: counts[o.value] || 0 }))
          );
        }

        if (needOther) {
          const closedFilter = {
            AND: [...filters.AND, { field: "status__current", operator: "eq", value: "closed" }],
          };
          const [statusAgg, severityAgg, entrywayAgg, closureAgg] = await Promise.all([
            eqlAggregate({ groupBy: "status.current", exclude: ["draft"], filters }),
            // severity is a nested dict { path: "type::hight" }; group by the path.
            eqlAggregate({ groupBy: "severity.path", filters }),
            eqlAggregate({ groupBy: "entryway", filters }),
            // closure type only exists on closed reports.
            eqlAggregate({ groupBy: "status.details.closed", filters: closedFilter }),
          ]);
          if (!active) return;

          const { status_locales: sl, severityOptions: sev, entryways: ew } = ctxRef.current;

          // Status donut is a nested pie: outer ring = status (incl. "Cerrado"),
          // inner ring = closure types of the closed reports.
          const statusData = [];
          const statusColors = [];
          (statusAgg?.data || []).forEach((r, i) => {
            statusData.push({ label: sl?.[r.value]?.label || String(r.value), count: r.count });
            statusColors.push(sl?.[r.value]?.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]);
          });

          // Closure-type inner ring (null closure -> "en proceso de cierre").
          const closureCounts = new Map();
          (closureAgg?.data || []).forEach((r) => {
            const v = r.value == null || r.value === "" ? "in_process" : r.value;
            closureCounts.set(v, (closureCounts.get(v) || 0) + r.count);
          });
          const closureOrdered = [
            ...CLOSURE_ORDER.filter((v) => closureCounts.has(v)),
            ...[...closureCounts.keys()].filter((v) => !CLOSURE_ORDER.includes(v)),
          ];
          const closureData = [];
          const closureColors = [];
          closureOrdered.forEach((v, i) => {
            closureData.push({
              label: t(`static_dashboard.closed_${v}`, String(v)),
              count: closureCounts.get(v),
            });
            closureColors.push(CLOSURE_COLOR[v] || DEFAULT_COLORS[i % DEFAULT_COLORS.length]);
          });

          // Severity (rose) — semantic colors, fixed order. A missing severity
          // (null path) is bucketed as "type::null" ("Sin definir").
          const sevCounts = new Map();
          (severityAgg?.data || []).forEach((r) => {
            const v = r.value || "type::null";
            sevCounts.set(v, (sevCounts.get(v) || 0) + r.count);
          });
          const sevLabel = (v) => sev.find((o) => o.value === v)?.label || String(v);
          // Only the known severities, in fixed order. Any other value is dropped.
          const severityData = [];
          const severityColors = [];
          SEVERITY_ORDER.forEach((v) => {
            if (!sevCounts.has(v)) return;
            severityData.push({ label: sevLabel(v), count: sevCounts.get(v) });
            severityColors.push(SEVERITY_COLOR[v]);
          });

          // Channel (entryway) — bar. Only the known entryways, in map order;
          // any other value is dropped.
          const chCounts = new Map((entrywayAgg?.data || []).map((r) => [r.value, r.count]));
          const channelData = Object.keys(ew)
            .filter((k) => chCounts.has(k))
            .map((k) => ({ label: ew[k].label, count: chCounts.get(k) }));

          otherCacheRef.current.set(year, {
            statusChart: {
              id: "by-status",
              title: t("static_dashboard.trends_by_status"),
              type: "donut",
              chartData: {
                title: t("static_dashboard.trends_by_status"),
                type: "normal",
                // Fallback single-series data (unused while rings render).
                data: statusData,
                rings: [
                  // Outer ring: status (incl. "Cerrado").
                  { name: t("static_dashboard.trends_by_status"), radius: ["46%", "68%"], data: statusData, colors: statusColors },
                  // Inner ring: closure types of the closed reports.
                  { name: t("static_dashboard.closure_type"), radius: ["20%", "40%"], data: closureData, colors: closureColors },
                ],
              },
            },
            severityChart: {
              id: "by-severity",
              title: t("static_dashboard.trends_by_severity"),
              type: "rose",
              colors: severityColors,
              chartData: { title: t("static_dashboard.trends_by_severity"), type: "normal", data: severityData },
            },
            channelChart: {
              id: "by-channel",
              title: t("static_dashboard.trends_by_channel"),
              type: "bar",
              chartData: { title: t("static_dashboard.trends_by_channel"), type: "normal", data: channelData },
            },
          });
        }

        if (!active) return;
        setTrends(buildTrends());
      } catch (e) {
        console.error("useStaticTrends: EQL query failed", e);
        if (active) setTrends([]);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
    // Only the selected year drives a refetch; context labels are stable per language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  return { trends, loading };
};

export default useStaticTrends;
